import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Star, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import StarBadge from '../components/StarBadge';
import Marquee from '../components/Marquee';
import { heroContent, locations, aboutContent } from '../data/mock';

const Home = () => {
  const products = [
    {
      id: 1,
      name: 'Berry Blast',
      description: 'Mixed berries with vanilla yogurt',
      image: 'https://customer-assets.emergentagent.com/job_77a0e81f-8f8a-48d0-b8f5-7f6a2e32faae/artifacts/g9pfqwv3_create-4-branded-frozen-yogurt-with-fruit-toppings%20%281%29.png',
      badge: 'BEST SELLER'
    },
    {
      id: 2,
      name: 'Waffle Delight',
      description: 'Crispy waffle with frozen yogurt',
      image: 'https://customer-assets.emergentagent.com/job_77a0e81f-8f8a-48d0-b8f5-7f6a2e32faae/artifacts/o2evi02k_create-a-branded-waffle-and-frozen-yogurt-with-and%20%282%29.png',
      badge: 'NEW'
    }
  ];

  return (
    <div className="min-h-screen bg-rush-cream">
      {/* Hero Section */}
      <section className="relative min-h-screen bg-rush-cream overflow-hidden pt-28">
        {/* Decorative blobs */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-rush-blue/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-rush-pink/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-rush-mint/20 rounded-full blur-3xl" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center min-h-[80vh]">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center bg-rush-mint/30 text-rush-navy rounded-full px-4 py-2 mb-6">
                <Sparkles size={16} className="mr-2" />
                <span className="text-sm font-semibold">Premium Frozen Yogurt</span>
              </div>
              
              <h1 className="text-5xl md:text-7xl lg:text-8xl font-display text-rush-navy leading-tight mb-6">
                chill out<br />
                <span className="text-rush-blue">with rush!</span>
              </h1>
              
              <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-md mx-auto lg:mx-0">
                Fresh fruit toppings. Creamy goodness. Pure happiness in every cup. 🍦
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link to="/locations">
                  <Button className="bg-rush-blue hover:bg-rush-blue/90 text-white font-bold px-8 py-6 text-lg rounded-full group">
                    Find a Store
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                  </Button>
                </Link>
                <Link to="/franchise">
                  <Button variant="outline" className="border-2 border-rush-navy text-rush-navy hover:bg-rush-navy hover:text-white font-bold px-8 py-6 text-lg rounded-full">
                    Become a Partner
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right Content - Floating Product */}
            <div className="relative flex justify-center items-center">
              {/* Star badges */}
              <div className="absolute -top-4 -left-4 lg:top-0 lg:left-0 z-20">
                <StarBadge text="BEST SELLER" color="bg-rush-pink" size="lg" />
              </div>
              <div className="absolute -bottom-4 -right-4 lg:bottom-10 lg:right-0 z-20">
                <StarBadge text="100% FRESH" color="bg-rush-mint" textColor="text-rush-navy" size="md" />
              </div>
              
              {/* Main product image */}
              <div className="relative animate-float">
                <div className="absolute inset-0 bg-rush-blue/20 rounded-full blur-3xl scale-75" />
                <img
                  src="https://customer-assets.emergentagent.com/job_77a0e81f-8f8a-48d0-b8f5-7f6a2e32faae/artifacts/o2evi02k_create-a-branded-waffle-and-frozen-yogurt-with-and%20%282%29.png"
                  alt="RUSH Frozen Yogurt"
                  className="relative z-10 w-full max-w-md lg:max-w-lg object-contain drop-shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee Section */}
      <Marquee text="FROZEN YOGURT" bgColor="bg-rush-blue" />
      
      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: '🍓', title: 'Fresh Fruits', desc: 'Daily picked' },
              { icon: '🥛', title: 'Real Yogurt', desc: 'No artificial' },
              { icon: '🌟', title: 'Premium Quality', desc: '5-star rated' },
              { icon: '💚', title: 'Healthy Choice', desc: 'Low calorie' }
            ].map((feature, idx) => (
              <div key={idx} className="bg-rush-cream rounded-3xl p-6 text-center hover:scale-105 transition-transform cursor-pointer">
                <span className="text-4xl mb-3 block">{feature.icon}</span>
                <h3 className="font-bold text-rush-navy">{feature.title}</h3>
                <p className="text-sm text-gray-500">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Preview */}
      <section className="py-20 bg-rush-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-flex items-center bg-rush-pink/20 text-rush-pink rounded-full px-4 py-2 mb-4">
              <Star size={16} className="mr-2 fill-rush-pink" />
              <span className="text-sm font-semibold">Our Favorites</span>
            </span>
            <h2 className="text-4xl md:text-5xl font-display text-rush-navy">
              taste the joy!
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {products.map((product) => (
              <div
                key={product.id}
                className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow group"
              >
                <div className="relative bg-gradient-to-br from-rush-blue/5 to-rush-pink/5 p-8">
                  <div className="absolute top-4 right-4">
                    <StarBadge 
                      text={product.badge} 
                      color={product.badge === 'NEW' ? 'bg-rush-mint' : 'bg-rush-pink'}
                      textColor={product.badge === 'NEW' ? 'text-rush-navy' : 'text-white'}
                      size="sm" 
                    />
                  </div>
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-64 object-contain group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-rush-navy mb-2">{product.name}</h3>
                  <p className="text-gray-500">{product.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Marquee Section */}
      <Marquee text="FRESH TOPPINGS" bgColor="bg-rush-pink" />

      {/* Locations Preview */}
      <section className="py-20 bg-rush-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-flex items-center bg-rush-mint/20 text-rush-mint rounded-full px-4 py-2 mb-4">
              <MapPin size={16} className="mr-2" />
              <span className="text-sm font-semibold">Our Locations</span>
            </span>
            <h2 className="text-4xl md:text-5xl font-display text-white">
              find us near you!
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {locations.map((location) => (
              <div
                key={location.id}
                className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 hover:bg-white/20 transition-all duration-300 text-center group hover:scale-105"
              >
                <span className="text-6xl mb-4 block">{location.flag}</span>
                <h3 className="text-2xl font-bold text-white mb-2">{location.country}</h3>
                <p className="text-white/70 mb-4">{location.city}</p>
                <div className="flex items-center justify-center space-x-2 text-rush-mint">
                  <MapPin size={16} />
                  <span className="text-sm">{location.address}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/locations">
              <Button className="bg-rush-mint text-rush-navy hover:bg-rush-mint/90 font-bold px-8 py-6 text-lg rounded-full">
                View All Locations
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-rush-cream relative overflow-hidden">
        <div className="absolute inset-0 opacity-50">
          <div className="absolute top-10 left-10 w-32 h-32 bg-rush-blue/20 rounded-full blur-2xl" />
          <div className="absolute bottom-10 right-10 w-48 h-48 bg-rush-pink/20 rounded-full blur-3xl" />
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-display text-rush-navy mb-6">
            join the rush family!
          </h2>
          <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto">
            Interested in bringing RUSH to your community? We're looking for passionate partners to expand our frozen yogurt experience worldwide. 🌍
          </p>
          <Link to="/franchise">
            <Button className="bg-rush-pink hover:bg-rush-pink/90 text-white font-bold px-10 py-6 text-lg rounded-full">
              Franchise Inquiry →
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
